import { useEffect, useMemo, useState, type FormEvent } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { SiteHeader } from "@/components/site-header"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Pagination } from "@/components/ui/pagination"
import { authorizedFetch } from "@/lib/api"
import { API_URL } from "@/lib/mutation-utils"
import { usePermissions } from "@/hooks/use-permissions"
import { PencilIcon, PlusIcon, SearchIcon, Trash2Icon } from "lucide-react"
import { RestoreIcon } from "@/components/icons/RestoreIcon"
import { isProtectedShipmentDocTypeCode } from "@cdc-sccr/shared"

const PAGE_SIZE = 25

type Control = "T" | "C"

type ShipmentDocTypeRow = {
  id: string
  code: string
  name: string
  control: Control | null
  sortOrder: number
  deletedAt: string | null
  createdAt: string
  updatedAt: string
}

const EMPTY_FORM = {
  code: "",
  name: "",
  control: "" as Control | "",
  sortOrder: "0",
}

type FilterStatus = "active" | "deleted" | "all"
type FormState = typeof EMPTY_FORM

const FILTER_STATUS_LABELS: Record<FilterStatus, string> = {
  active: "Activos",
  deleted: "Eliminados",
  all: "Todos",
}

const CONTROL_LABELS: Record<Control | "none", string> = {
  none: "Sin control",
  T: "T",
  C: "C",
}

export default function ShipmentDocTypesPage() {
  const qc = useQueryClient()
  const { can } = usePermissions()
  const canWrite = can("write", "shipmentDocTypes")

  const [search, setSearch] = useState("")
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("active")
  const [page, setPage] = useState(1)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [editing, setEditing] = useState<ShipmentDocTypeRow | null>(null)
  const [deleting, setDeleting] = useState<ShipmentDocTypeRow | null>(null)
  const [restoringId, setRestoringId] = useState<string | null>(null)
  const [form, setForm] = useState<FormState>({ ...EMPTY_FORM })
  const [formError, setFormError] = useState<string | null>(null)

  const { data: docTypes = [], isLoading } = useQuery({
    queryKey: ["shipment-doc-types", filterStatus],
    queryFn: async () => {
      const query: Record<string, string> = {}
      if (filterStatus === "active") query.deleted = "false"
      else if (filterStatus === "deleted") query.deleted = "true"
      const params = new URLSearchParams(query)
      const res = await authorizedFetch(`${API_URL}/api/shipment-doc-types?${params}`)
      return res.json() as Promise<ShipmentDocTypeRow[]>
    },
  })

  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim()
    if (!term) return docTypes
    return docTypes.filter(item =>
      item.code.toLowerCase().includes(term) || item.name.toLowerCase().includes(term),
    )
  }, [docTypes, search])

  useEffect(() => setPage(1), [search, filterStatus])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const createMut = useMutation({
    mutationFn: async (data: FormState) => {
      const res = await authorizedFetch(`${API_URL}/api/shipment-doc-types`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code: data.code,
          name: data.name,
          control: data.control || null,
          sortOrder: Number(data.sortOrder),
        }),
      })
      return res.json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shipment-doc-types"] })
      closeSheet()
      toast.success("Tipo de documento creado")
    },
    onError: (err: Error) => setFormError(err.message),
  })

  const updateMut = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: FormState }) => {
      const res = await authorizedFetch(`${API_URL}/api/shipment-doc-types/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code: data.code,
          name: data.name,
          control: data.control || null,
          sortOrder: Number(data.sortOrder),
        }),
      })
      return res.json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shipment-doc-types"] })
      closeSheet()
      toast.success("Tipo de documento actualizado")
    },
    onError: (err: Error) => setFormError(err.message),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      await authorizedFetch(`${API_URL}/api/shipment-doc-types/${id}`, { method: "DELETE" })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shipment-doc-types"] })
      setDeleteOpen(false)
      setDeleting(null)
      toast.success("Tipo de documento eliminado")
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const restoreMut = useMutation({
    mutationFn: async (id: string) => {
      setRestoringId(id)
      await authorizedFetch(`${API_URL}/api/shipment-doc-types/${id}/restore`, { method: "PATCH" })
    },
    onSettled: () => setRestoringId(null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shipment-doc-types"] })
      toast.success("Tipo de documento restaurado")
    },
    onError: (err: Error) => toast.error(err.message),
  })

  function closeSheet() {
    setSheetOpen(false)
    setEditing(null)
    setForm({ ...EMPTY_FORM })
    setFormError(null)
  }

  function openCreate() {
    setEditing(null)
    setForm({ ...EMPTY_FORM })
    setFormError(null)
    setSheetOpen(true)
  }

  function openEdit(item: ShipmentDocTypeRow) {
    setEditing(item)
    setForm({
      code: item.code,
      name: item.name,
      control: item.control ?? "",
      sortOrder: String(item.sortOrder),
    })
    setFormError(null)
    setSheetOpen(true)
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setFormError(null)
    const payload: FormState = {
      code: form.code.trim().toUpperCase(),
      name: form.name.trim(),
      control: form.control,
      sortOrder: form.sortOrder.trim(),
    }

    if (!payload.code || !payload.name) {
      setFormError("Completá código y nombre")
      return
    }
    if (!/^\d+$/.test(payload.sortOrder) || Number(payload.sortOrder) > 9999) {
      setFormError("El orden debe ser un número entero entre 0 y 9999")
      return
    }

    if (editing) updateMut.mutate({ id: editing.id, data: payload })
    else createMut.mutate(payload)
  }

  const isSaving = createMut.isPending || updateMut.isPending

  return (
    <>
      <SiteHeader title="Tipos de documentos de viaje" />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative min-w-[220px] flex-1">
            <SearchIcon className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar por código o nombre..."
              value={search}
              onChange={event => setSearch(event.target.value)}
              className="pl-8"
            />
          </div>
          <Select value={filterStatus} onValueChange={value => setFilterStatus(value as FilterStatus)}>
            <SelectTrigger className="w-40" size="sm"><SelectValue>{FILTER_STATUS_LABELS[filterStatus]}</SelectValue></SelectTrigger>
            <SelectContent>
              {(Object.keys(FILTER_STATUS_LABELS) as FilterStatus[]).map(status => (
                <SelectItem key={status} value={status}>{FILTER_STATUS_LABELS[status]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {canWrite && (
            <Button onClick={openCreate} className="gap-2">
              <PlusIcon className="size-4" /> Nuevo tipo
            </Button>
          )}
        </div>

        <div className="overflow-hidden rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-28">Código</TableHead>
                <TableHead>Nombre</TableHead>
                <TableHead className="w-28">Control</TableHead>
                <TableHead className="w-24">Orden</TableHead>
                {canWrite && <TableHead className="w-32 text-right pr-4">Acciones</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={canWrite ? 5 : 4} className="py-16 text-center text-muted-foreground">Cargando tipos de documentos...</TableCell></TableRow>
              ) : paged.length === 0 ? (
                <TableRow><TableCell colSpan={canWrite ? 5 : 4} className="py-16 text-center text-muted-foreground">{search ? "No hay resultados" : "No hay tipos de documentos"}</TableCell></TableRow>
              ) : paged.map(item => (
                <TableRow key={item.id} className={item.deletedAt ? "opacity-70" : undefined}>
                  <TableCell><span className="rounded bg-muted px-2 py-0.5 font-mono text-xs tracking-wide">{item.code}</span></TableCell>
                  <TableCell className="text-sm font-medium">{item.name}</TableCell>
                  <TableCell>
                    {item.control ? <span className="rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-700">{item.control}</span> : <span className="text-sm text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-sm tabular-nums text-muted-foreground">{item.sortOrder}</TableCell>
                  {canWrite && (
                    <TableCell className="pr-4 text-right">
                      <div className="flex justify-end gap-1">
                        {item.deletedAt ? (
                          <Button variant="ghost" size="icon-sm" onClick={() => restoreMut.mutate(item.id)} disabled={restoringId === item.id} className="text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700" title="Restaurar">
                            <RestoreIcon className="size-3.5" />
                          </Button>
                        ) : (
                          <>
                            <Button variant="ghost" size="icon-sm" onClick={() => openEdit(item)} title="Editar"><PencilIcon className="size-3.5" /></Button>
                            <Button variant="ghost" size="icon-sm" onClick={() => { setDeleting(item); setDeleteOpen(true) }} disabled={isProtectedShipmentDocTypeCode(item.code)} className="text-destructive hover:bg-destructive/10 hover:text-destructive" title={isProtectedShipmentDocTypeCode(item.code) ? "Tipo de sistema, no se puede eliminar" : "Eliminar"}><Trash2Icon className="size-3.5" /></Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination currentPage={page} totalPages={totalPages} totalItems={filtered.length} pageSize={PAGE_SIZE} onPageChange={setPage} />
        </div>
      </div>

      <Sheet open={sheetOpen} onOpenChange={open => { if (!open) closeSheet(); else setSheetOpen(true) }}>
        <SheetContent side="right" className="flex flex-col overflow-y-auto sm:max-w-sm">
          <SheetHeader>
            <SheetTitle>{editing ? "Editar tipo de documento de viaje" : "Nuevo tipo de documento de viaje"}</SheetTitle>
            <SheetDescription>Configurá el código, nombre y control del documento.</SheetDescription>
          </SheetHeader>
          <form onSubmit={handleSubmit} className="flex flex-1 flex-col gap-4 px-4 pb-4">
            <div className="grid gap-1.5">
              <Label htmlFor="shipment-doc-type-code">Código <span className="text-destructive">*</span></Label>
              <Input id="shipment-doc-type-code" value={form.code} disabled={!!editing && isProtectedShipmentDocTypeCode(editing.code)} onChange={event => setForm(current => ({ ...current, code: event.target.value }))} maxLength={10} className="font-mono uppercase" placeholder="RC" required />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="shipment-doc-type-name">Nombre <span className="text-destructive">*</span></Label>
              <Input id="shipment-doc-type-name" value={form.name} onChange={event => setForm(current => ({ ...current, name: event.target.value }))} maxLength={200} placeholder="Remito Carga" required />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="shipment-doc-type-control">Control</Label>
              <Select value={form.control || "none"} onValueChange={value => setForm(current => ({ ...current, control: value === "none" ? "" : value as Control }))}>
                <SelectTrigger id="shipment-doc-type-control"><SelectValue>{CONTROL_LABELS[form.control || "none"]}</SelectValue></SelectTrigger>
                <SelectContent>
                  {(Object.keys(CONTROL_LABELS) as (Control | "none")[]).map(value => (
                    <SelectItem key={value} value={value}>{CONTROL_LABELS[value]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="shipment-doc-type-order">Orden</Label>
              <Input id="shipment-doc-type-order" type="number" min="0" max="9999" step="1" value={form.sortOrder} onChange={event => setForm(current => ({ ...current, sortOrder: event.target.value }))} />
            </div>
            {formError && <p className="rounded bg-destructive/10 px-3 py-2 text-sm text-destructive">{formError}</p>}
            <SheetFooter className="mt-auto px-0 pt-4">
              <Button type="button" variant="outline" onClick={closeSheet}>Cancelar</Button>
              <Button type="submit" disabled={isSaving}>{isSaving ? "Guardando..." : editing ? "Guardar cambios" : "Crear tipo"}</Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Eliminar tipo de documento de viaje</DialogTitle>
            <DialogDescription>¿Eliminás <strong>{deleting?.code}</strong> — {deleting?.name}? Esta acción es reversible.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose><Button variant="outline" onClick={() => setDeleting(null)}>Cancelar</Button></DialogClose>
            <Button variant="destructive" disabled={deleteMut.isPending} onClick={() => deleting && deleteMut.mutate(deleting.id)}>{deleteMut.isPending ? "Eliminando..." : "Eliminar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
